import {
    Button,
    HStack,
    Text,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { AccessIssuesEntryMethodDefinitions } from '@acp/shared';
import { useNextProblemSet } from '../hooks';
import { AccessIssuesWizardMachineContext } from '../machine/context';
import { AccessIssuesWizardEvents } from '../machine/machine';

/**
 * This figures out whether we should allow them
 * to change the entry method they're currently resolving
 *
 * It'll allow them to change if:
 * - There's another entry method other than the one they're currently resolving
 *
 */
function useEntryMethodLink() {
    const currentEntryMethodName = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.entryMethods.current
    );

    const availableEntryMethods = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.entryMethods.available
    );

    const resolvedEntryMethods = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.entryMethods.resolved
    );

    const blockedEntryMethodQueues = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.blockedEntryMethodQueues
    );

    const link = useMemo<{
        actionText: string;
        action: AccessIssuesWizardEvents;
    } | null>(() => {
        const entryMethodDetails = Object.values(blockedEntryMethodQueues).map((queue) => ({
            entryMethod: queue.blocked_entry_method,
            isBlocked: queue.is_blocked_by_preferences,
            isCurrent: queue.blocked_entry_method === currentEntryMethodName,
            isResolved: resolvedEntryMethods.includes(queue.blocked_entry_method),
        }));

        const otherEntryMethods = entryMethodDetails.filter(
            (entryMethod) => !entryMethod.isCurrent
                && !entryMethod.isResolved
        );

        if (otherEntryMethods.length === 0) {
            return null;
        }

        if (otherEntryMethods.length === 1) {
            const method = otherEntryMethods[0].entryMethod;
            return {
                actionText: `Would you like to arrange an ${AccessIssuesEntryMethodDefinitions[method].name.toLowerCase()} instead?`,
                action: {
                    type: 'SELECT_ENTRY_METHOD',
                    entryMethod: method,
                },
            };
        }

        return {
            actionText: 'Would you like to arrange a different kind of booking instead?',
            action: { type: 'UNBLOCK_OTHER_ENTRY_METHOD' },
        };
    }, [
        currentEntryMethodName,
        availableEntryMethods,
        blockedEntryMethodQueues,
        resolvedEntryMethods,
    ]);

    return link;
}

function AccessIssuesWizardChangeEntryMethodLink() {
    const machine = AccessIssuesWizardMachineContext.useActorRef();
    const link = useEntryMethodLink();

    const nextProblemSet = useNextProblemSet();
    const isResolutionCompleted = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.matches('resolveIssueCompleted')
    );

    const isLinkDisabled = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.matches('resolveIssuePending')
    );

    if (nextProblemSet === null && isResolutionCompleted) {
        return null;
    }

    if (link === null) return null;

    return (
        <HStack color="muted.500" gap={1} pt={5}>
            <Text fontSize="sm">{link.actionText}</Text>
            <Button
                variant="link"
                fontSize="sm"
                color="muted.700"
                fontWeight="medium"
                p={0}
                onClick={() => machine.send(link.action)}
                isDisabled={isLinkDisabled}
            >
                Click here
            </Button>
        </HStack>
    );
}

export { AccessIssuesWizardChangeEntryMethodLink };
